import { Injectable } from '@angular/core';
import { ProcedureEnum } from '@core/procedure-helpers/models/procedure.enum';
import { ProcedureMap } from '@shared/models/procedure-map';
import { ProcedureFormState } from '@shared/models/procedure-form-state';
import { FormControlStatus } from '@shared/models/form-control-status';
import { TypeEnum } from '@modules/order/models/type.enum';
import { IdName } from '@shared/models/id-name';
import { TreatmentStage } from '@shared/models/treatment-stage';
import { SendToTypeEnum } from '@modules/order/models/send-to-type.enum';
import { RxTemplate } from '@shared/rx-templates/rx-template.model';
import { isProcedureTypeHiddenByTemplate } from '@shared/rx-templates/rx-template-utils';
import { Maybe } from '@/types/globals';

const SEND_TO_NOT_REQUIRED_FOR_RESEND_PROCEDURES: ReadonlySet<ProcedureEnum> = new Set([
	ProcedureEnum.StudyModel_iRecord,
	ProcedureEnum.Appliance,
	ProcedureEnum.Invisalign
]);

@Injectable({
	providedIn: 'root'
})
export class ProcedureFormStateService {
	getFormState(
		procedureMap: Maybe<ProcedureMap>,
		isReadOnly: boolean,
		treatmentStage: Maybe<IdName | TreatmentStage>,
		shouldValidateForSend: boolean,
		procedureMaps: Maybe<ProcedureMap[]>,
		template: RxTemplate
	): ProcedureFormState {
		return {
			sendTo: this.getSendToState(procedureMap, shouldValidateForSend),
			treatmentStage: this.getTreatmentStageState(procedureMap),
			dueDate: this.getDueDateState(procedureMap, isReadOnly, template),
			currentAlignerId: this.getCurrentAlignerIdState(procedureMap, treatmentStage),
			// Fields below remain inline as they don't require separate methods
			iCastOrthoModel: {
				// NOTE: iCastOrthoModel is now handled separately in scan-options module
				isVisible: this.getIcastOrthoModelVisibility(procedureMap, template),
				isEnabled: true
			},
			type: {
				isVisible: !this.isIcastOrthoModelVisible(procedureMap),
				isEnabled:
					this.isTypeEnabled(procedureMap, procedureMaps) && !isProcedureTypeHiddenByTemplate(procedureMap?.ProcedureId, template)
			}
		};
	}

	/**
	 * Get the state of the SendTo field
	 * @param procedureMap - Current procedure map configuration
	 * @param shouldValidateForSend - Whether to validate for send operation
	 * @param isResendScan - Whether the current flow is a resend scan (makes SendTo mandatory)
	 * @returns Field state with visibility, enabled, and required flags
	 */
	getSendToState(procedureMap: Maybe<ProcedureMap>, shouldValidateForSend: boolean, isResendScan = false): FormControlStatus {
		return {
			isVisible: this.isSendToVisible(procedureMap),
			isEnabled: this.isSendToEnabled(procedureMap, isResendScan),
			isRequired: this.isSendToRequired(procedureMap, shouldValidateForSend, isResendScan)
		};
	}

	/**
	 * Get the state of the TreatmentStage field
	 * @param procedureMap - Current procedure map configuration
	 * @returns Field state with visibility and enabled flags
	 */
	getTreatmentStageState(procedureMap: Maybe<ProcedureMap>): FormControlStatus {
		return {
			isVisible: this.isTreatmentStageVisible(procedureMap),
			isEnabled: this.isTreatmentStageEnabled(procedureMap)
		};
	}

	getDueDateState(procedureMap: ProcedureMap | null | undefined, isReadOnly: boolean, template: RxTemplate): FormControlStatus {
		return {
			isVisible: this.isDueDateRelevant(procedureMap),
			isEnabled: !!procedureMap?.DueDateIsEnabled && !template.ui.order.items.dueDate.disabled,
			isRequired: !isReadOnly ? this.isDueDateRequired(procedureMap) : false
		};
	}

	isDueDateRelevant(procedureMap: ProcedureMap | null | undefined): boolean {
		return procedureMap
			? (procedureMap.ProcedureId as ProcedureEnum) !== ProcedureEnum.Invisalign || !!procedureMap.DueDateIsEnabled
			: false;
	}

	getIcastOrthoModelVisibility(procedureMap: Maybe<ProcedureMap>, template: RxTemplate): boolean {
		return this.isIcastOrthoModelVisible(procedureMap) && !template.ui.scanOptions?.items.procedureType?.hidden;
	}

	/**
	 * Get the state of the CurrentAlignerId field
	 * @param procedureMap - Current procedure map configuration
	 * @param treatmentStage - Current treatment stage
	 * @returns Field state with visibility and enabled flags
	 */
	getCurrentAlignerIdState(procedureMap: Maybe<ProcedureMap>, treatmentStage: Maybe<IdName | TreatmentStage>): FormControlStatus {
		return {
			isVisible: this.isCurrentAlignerIdVisible(procedureMap),
			isEnabled: this.isCurrentAlignerIdEnabled(treatmentStage)
		};
	}

	/**
	 * Check if SendTo field can be cancelled (cleared)
	 * @param procedureMap - Current procedure map configuration
	 * @param availableProcedureMaps - All available procedure maps
	 * @returns True if SendTo can be cancelled
	 */
	isSendToCancellable(procedureMap: Maybe<ProcedureMap>, availableProcedureMaps: Maybe<ProcedureMap[]>): boolean {
		if (!procedureMap || !availableProcedureMaps) {
			return false;
		}

		return availableProcedureMaps.some(
			pm =>
				(pm?.SendToTypes as SendToTypeEnum) === SendToTypeEnum.empty &&
				pm?.ProcedureId === procedureMap?.ProcedureId &&
				pm?.TypeId === procedureMap?.TypeId
		);
	}

	private isSendToEnabled(procedureMap: ProcedureMap | null | undefined, isResendScan: boolean): boolean {
		return !!procedureMap?.SendToIsEnabled && !(isResendScan && !this.isSendToRequiredForResend(procedureMap));
	}

	private isSendToVisible(procedureMap: Maybe<ProcedureMap>): boolean {
		return procedureMap ? ![ProcedureEnum.Invisalign].includes(procedureMap.ProcedureId) : false;
	}

	private isSendToRequired(procedureMap: ProcedureMap | null | undefined, shouldValidate: boolean, isResendScan: boolean): boolean {
		if (isResendScan) {
			return this.isSendToRequiredForResend(procedureMap);
		}

		return shouldValidate && !!procedureMap?.SendToIsMandatory;
	}

	isSendToRequiredForResend(procedureMap: ProcedureMap | null | undefined): boolean {
		return !SEND_TO_NOT_REQUIRED_FOR_RESEND_PROCEDURES.has(procedureMap?.ProcedureId as ProcedureEnum);
	}

	private isIcastOrthoModelVisible(procedureMap: Maybe<ProcedureMap>): boolean {
		const availableProcedures = [ProcedureEnum.StudyModel_iRecord];

		return procedureMap ? availableProcedures.includes(procedureMap.ProcedureId) : false;
	}

	private isTreatmentStageVisible(procedureMap: Maybe<ProcedureMap>): boolean {
		return procedureMap ? [ProcedureEnum.Invisalign].includes(procedureMap.ProcedureId) : false;
	}

	private isTreatmentStageEnabled(procedureMap: Maybe<ProcedureMap>): boolean {
		if (!procedureMap) {
			return false;
		}

		const byType = [
			TypeEnum.Invisalign_Aligners,
			TypeEnum.Invisalign_First_Aligners,
			TypeEnum.Invisalign_First_Palatal_Expander
		].includes(procedureMap.TypeId);

		return byType;
	}

	private isDueDateRequired(procedureMap: Maybe<ProcedureMap>): boolean {
		// @ts-expect-error - TODO: check if this logic still makes sense
		return [].includes(procedureMap);
	}

	private isCurrentAlignerIdVisible(procedureMap: Maybe<ProcedureMap>): boolean {
		return procedureMap ? [ProcedureEnum.Invisalign].includes(procedureMap.ProcedureId) : false;
	}

	private isCurrentAlignerIdEnabled(treatmentStage: Maybe<IdName | TreatmentStage>): boolean {
		return !!(treatmentStage as TreatmentStage)?.ClearAlignerIsEnabled;
	}

	private isTypeEnabled(procedureMap: Maybe<ProcedureMap>, procedureMaps: Maybe<ProcedureMap[]>): boolean {
		if (!procedureMap || procedureMaps?.length === 0) {
			return false;
		}

		const filteredProcedureMaps = procedureMaps?.filter(x => x.ProcedureId === procedureMap.ProcedureId) ?? [];

		return filteredProcedureMaps.some(x => (x.TypeId as TypeEnum) > TypeEnum.Empty);
	}
}
